<template lang="html">
  <div class="search-suggests flex flex-col gap-4">
    <!-- Категории -->
    <div v-if="filteredCategories.length" class="search-suggests__categories">
      <p class="text-sm font-semibold text-neutral-500">
        Категории
      </p>
      <div class="flex flex-col">
        <UButton
          v-for="category in filteredCategories"
          :key="category.id"
          variant="link"
          color="neutral"
          class="justify-start"
          @click="goToCategory(category)"
        >
          {{ category.displayName }}
        </UButton>
      </div>
    </div>

    <!-- Товары -->
    <div
      v-if="productsPending"
      class="search-suggests__products-loading flex justify-center py-4"
    >
      <UIcon
        name="i-lucide-loader-circle"
        class="animate-spin text-2xl text-neutral-400"
      />
    </div>

    <div v-else-if="filteredProducts.length" class="search-suggests__products">
      <p class="text-sm font-semibold text-neutral-500">
        Товары
      </p>
      <div class="flex flex-col">
        <UButton
          v-for="product in filteredProducts"
          :key="product.id"
          variant="link"
          color="neutral"
          class="justify-start"
          @click="goToProduct(product)"
        >
          <template #leading>
            <UiPicture
              :src="product.images?.[0]?.id"
              variant="imageFull"
              sizes="40px"
              class="h-8 w-8"
            />
          </template>
          {{ product.wellKnownAttributes?.Name || product.id }}
        </UButton>
      </div>
    </div>

    <!-- Найти всё -->
    <UButton
      v-if="props.query"
      variant="ghost"
      color="primary"
      class="mt-2 w-full justify-center"
      @click="goToSearch"
    >
      Найти всё
    </UButton>
  </div>
</template>

<script setup lang="ts">
import { ProductListItemDto } from '@repo/api/web/models'
import {
  EntityRef,
  type ProductsListGetRequestQuery,
} from '@repo/api/web/models'

import type { CategoryDto } from '#Products/models/Pim/CategoryDto'
import UiPicture from '~/app/components/Image/UiPicture.vue'

const props = defineProps<{
  query: string
}>()

const emit = defineEmits<{
  select: []
}>()

const { service, routing } = useProducts()
const { byIds } = await useCategories()

const filteredCategories = computed(() => {
  if (!props.query || productsPending.value) return []

  const query = props.query.toLowerCase()
  const found = Object.values(byIds.value)
    .filter((category: CategoryDto) =>
      category.name.toLowerCase().includes(query),
    )
    .slice(0, 6)
    .map(category =>
      EntityRef.createLazy({ id: category.id, displayName: category.name }),
    )
  const categoriesMap = new Map<number, EntityRef>(
    found.map(category => [category.id, category]),
  )

  for (const category of iterateProductsCategories()) {
    if (categoriesMap.size >= 6) break

    if (category && !categoriesMap.has(category.id)) {
      categoriesMap.set(category.id, category)
    }
  }

  return Array.from(categoriesMap.values())
})

function* iterateProductsCategories() {
  if (!productsData.value?.items) yield undefined

  for (const product of productsData.value?.items || []) {
    for (const category of product.categories || []) {
      yield category
    }
  }
}

const { data: productsData, pending: productsPending } = await useAsyncData(
  `search-suggests-${props.query}`,
  () =>
    service.list({
      Query: props.query,
      Limit: 6,
    } as ProductsListGetRequestQuery),
  {
    watch: [() => props.query],
    server: false,
  },
)

const filteredProducts = computed(() => {
  return ProductListItemDto.createFromCollection(
    productsData.value?.items ?? [],
  )
})

const goToCategory = (category: EntityRef) => {
  emit('select')
  navigateTo(
    routing.getCatalog({
      id: category.id,
      displayName: category.displayName,
    } as EntityRef).route,
  )
}

const goToProduct = (product: ProductListItemDto) => {
  emit('select')
  navigateTo(routing.getDetails(product).route)
}

const goToSearch = () => {
  emit('select')
  navigateTo(routing.getSearch(props.query).route)
}
</script>
